import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format, parseISO } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Users, CheckCircle, XCircle, Clock, Eye, Brain, Loader2, Trash2, Link } from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700", icon: Clock },
  processing: { label: "Processing", color: "bg-blue-100 text-blue-700", icon: Loader2 },
  pass: { label: "Pass", color: "bg-green-100 text-green-700", icon: CheckCircle },
  review: { label: "Review", color: "bg-amber-100 text-amber-700", icon: Eye },
  fail: { label: "Fail", color: "bg-red-100 text-red-700", icon: XCircle },
  evaluated: { label: "Evaluated", color: "bg-indigo-100 text-indigo-700", icon: Brain },
};

const DECISION_CONFIG = {
  hired: { label: "HIRED", color: "bg-green-600 text-white" },
  rejected: { label: "REJECTED", color: "bg-red-600 text-white" },
};

export default function Recruitment() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [decisionFilter, setDecisionFilter] = useState("all");

  const fetchApplicants = useCallback(async () => {
    try {
      let url = `${API}/recruitment/applicants`;
      const params = [];
      if (statusFilter !== "all") params.push(`status=${statusFilter}`);
      if (decisionFilter !== "all") params.push(`decision=${decisionFilter}`);
      if (params.length) url += "?" + params.join("&");
      const res = await axios.get(url, { headers: getAuthHeader() });
      setApplicants(res.data);
    } catch (error) {
      toast.error("Failed to load applicants");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, statusFilter, decisionFilter]);

  useEffect(() => { fetchApplicants(); }, [fetchApplicants]);

  const handleEvaluate = async (id, e) => {
    e.stopPropagation();
    try {
      toast.info("Re-running AI evaluation...");
      const res = await axios.post(`${API}/recruitment/applicants/${id}/evaluate`, {}, { headers: getAuthHeader(), timeout: 180000 });
      toast.success(`Evaluation complete! Score: ${res.data.overall_score}`);
      fetchApplicants();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Evaluation failed");
    }
  };

  const handleEvaluatePending = async () => {
    try {
      const res = await axios.post(`${API}/recruitment/evaluate-pending`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      // Poll for updates
      setTimeout(fetchApplicants, 5000);
      setTimeout(fetchApplicants, 15000);
      setTimeout(fetchApplicants, 30000);
    } catch (error) {
      toast.error("Failed to start evaluations");
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!confirm("Delete this applicant?")) return;
    try {
      await axios.delete(`${API}/recruitment/applicants/${id}`, { headers: getAuthHeader() });
      toast.success("Applicant deleted");
      fetchApplicants();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const filtered = applicants.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.email.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: applicants.length,
    pass: applicants.filter(a => a.status === "pass").length,
    review: applicants.filter(a => a.status === "review").length,
    fail: applicants.filter(a => a.status === "fail").length,
    hired: applicants.filter(a => a.admin_decision === "hired").length,
  };

  const appUrl = window.location.origin;

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Recruitment</h1>
          <p className="text-slate-500">AI-powered tutor hiring pipeline</p>
        </div>
        <div className="flex gap-2">
          {stats.total > 0 && applicants.some(a => a.status === "pending") && (
            <Button variant="default" size="sm" onClick={handleEvaluatePending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="evaluate-pending-btn">
              <Brain className="w-4 h-4 mr-2" /> Evaluate Pending ({applicants.filter(a => a.status === "pending").length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${appUrl}/apply`); toast.success("Application link copied!"); }} data-testid="copy-link-btn">
            <Link className="w-4 h-4 mr-2" /> Copy Application Link
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total, color: "text-slate-700" },
          { label: "Pass", value: stats.pass, color: "text-green-700" },
          { label: "Review", value: stats.review, color: "text-amber-700" },
          { label: "Fail", value: stats.fail, color: "text-red-700" },
          { label: "Hired", value: stats.hired, color: "text-indigo-700" },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-4 text-center">
              <div className={cn("text-2xl font-bold", s.color)}>{s.value}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="pl-9" data-testid="search-applicants" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="pass">Pass</SelectItem>
            <SelectItem value="review">Review</SelectItem>
            <SelectItem value="fail">Fail</SelectItem>
          </SelectContent>
        </Select>
        <Select value={decisionFilter} onValueChange={setDecisionFilter}>
          <SelectTrigger className="w-40" data-testid="decision-filter">
            <SelectValue placeholder="Decision" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Decisions</SelectItem>
            <SelectItem value="hired">Hired</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Applicant Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold">Applicant</TableHead>
                <TableHead className="font-bold">Submitted</TableHead>
                <TableHead className="font-bold text-center">Score</TableHead>
                <TableHead className="font-bold text-center">Status</TableHead>
                <TableHead className="font-bold text-center">Decision</TableHead>
                <TableHead className="font-bold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No applicants found</TableCell></TableRow>
              ) : (
                filtered.map(a => {
                  const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.pending;
                  const dc = a.admin_decision ? DECISION_CONFIG[a.admin_decision] : null;
                  return (
                    <TableRow key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/recruitment/${a.id}`)} data-testid={`applicant-row-${a.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{a.name}</div>
                        <div className="text-xs text-slate-500">{a.email}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{format(parseISO(a.created_at), "MMM dd, yyyy")}</TableCell>
                      <TableCell className="text-center">
                        {a.overall_score != null ? (
                          <span className={cn("text-lg font-bold", a.overall_score >= 75 ? "text-green-600" : a.overall_score >= 60 ? "text-amber-600" : "text-red-600")}>
                            {Math.round(a.overall_score)}
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={cn("text-xs", sc.color)}>{sc.label}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {dc ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <Badge className={dc.color}>{dc.label}</Badge>
                            {a.ai_decision && a.admin_decision && a.ai_decision !== a.admin_decision && (
                              <span className="text-[10px] text-indigo-600 font-medium">overridden</span>
                            )}
                          </div>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {a.status === "pending" && (
                            <Button variant="outline" size="sm" onClick={(e) => handleEvaluate(a.id, e)} className="text-indigo-600 h-8" data-testid={`evaluate-btn-${a.id}`}>
                              <Brain className="w-3.5 h-3.5 mr-1" /> Retry
                            </Button>
                          )}
                          {a.status === "processing" && (
                            <span className="flex items-center gap-1 text-xs text-blue-600"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Evaluating...</span>
                          )}
                          <Button variant="ghost" size="sm" onClick={(e) => handleDelete(a.id, e)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
